import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TrackReference, TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { ParticipantVideoTile } from './ParticipantVideoTile.js';
import { tileKey } from './track-references.js';
import { firstTileInView, rowWindow, visibleTileCount } from './tile-row.js';
import { VIDEO_CONFIG, getStackPositionClass } from '../../config/videoConfig.js';
import { useOpenDrawers } from '../../hooks/useOpenDrawers.js';

/**
 * The most tiles the heads-up row shows at once; the rest are reached by
 * scrolling it sideways. Fewer show when fewer fit in the free width: 3 at
 * 1440x900, 4 at 1920x1080 (#131). It was six when tiles were half the size
 * (#106).
 */
export const MAX_VISIBLE_TILES = 4;

/**
 * Props for {@link VideoTileGrid}
 */
interface VideoTileGridProps {
  /**
   * Tiles to render, already in their stable order (local first, then join
   * order; see `orderTiles`). Tiles past the ones in view are reached by
   * scrolling the row.
   */
  trackRefs: TrackReferenceOrPlaceholder[];

  /**
   * Subscribed microphone per participant identity, for the speaking ring.
   * Only a participant's own camera tile gets it, never their screen share.
   */
  microphoneTrackRefs?: ReadonlyMap<string, TrackReference>;

  /** Override the tile cap (tests, future density setting) */
  maxVisibleTiles?: number;

  /**
   * Controls for the line under the tiles, left of the `+N` chip: the "Full
   * screen" pill (#123). The line renders whenever there are actions, even
   * with no chip. They get pointer events like the chip does.
   */
  actions?: React.ReactNode;
}

/** What the last measurement found: see {@link visibleTileCount}. */
interface RowGeometry {
  /** Whole tiles in view */
  visible: number;
  /** One tile plus one gap, in px; 0 until measured */
  stride: number;
}

/** Arrow buttons at the two ends of the row. */
const ARROW_CLASS =
  'pointer-events-auto absolute top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center ' +
  'rounded-full border border-game-medium bg-game-navy/90 text-game-cream shadow-lg transition-colors ' +
  'hover:bg-game-navy hover:text-game-golden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-game-golden';

/**
 * The heads-up display of a call: one row of participant tiles docked at the
 * top of the screen, centred in the width no drawer covers (#131).
 *
 * The row shows as many whole tiles as fit (at most {@link MAX_VISIBLE_TILES})
 * and scrolls sideways, one tile at a time, to reach everyone else. Most mice
 * cannot scroll sideways, so arrow buttons at both ends appear whenever there
 * is something in that direction, and the `+N` chip under the row counts the
 * tiles still off to the right. Trackpads and Shift+wheel scroll it natively,
 * and scroll-snap lands every scroll on a whole tile. The order is the stable
 * `orderTiles` order, so nothing jumps while scrolling and people who join are
 * appended at the end. This replaces the `+N` chip that expanded the stack
 * into a vertical list growing down over the world.
 *
 * Every tile stays mounted. `adaptiveStream` watches each video element with
 * an IntersectionObserver, which accounts for the row's clipping, so a tile
 * scrolled out of view stops receiving video anyway.
 *
 * LiveKit's own `GridLayout` and `CarouselLayout` both size themselves from a
 * full-height container, which a HUD is not, so the layout is hand-rolled;
 * only the tile internals use LiveKit components.
 *
 * The container is `pointer-events-none` so that clicks in the gaps fall
 * through to the Phaser map; the tiles, the arrows, the chip and the actions
 * opt back in.
 */
const VideoTileGridComponent: React.FC<VideoTileGridProps> = ({
  trackRefs,
  microphoneTrackRefs,
  maxVisibleTiles = MAX_VISIBLE_TILES,
  actions
}) => {
  const openDrawers = useOpenDrawers();
  const stripRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const forwardButtonRef = useRef<HTMLButtonElement>(null);

  const total = trackRefs.length;
  const [geometry, setGeometry] = useState<RowGeometry>(() => ({
    visible: Math.max(1, Math.min(maxVisibleTiles, total)),
    stride: 0
  }));
  const [start, setStart] = useState(0);

  /**
   * Measures the free width and the tiles, and derives how many tiles fit and
   * which one is first in view. The tile size follows the viewport (`vh`,
   * `vw`) and the free width follows the drawers, so both are observed.
   */
  const measure = useCallback(() => {
    const strip = stripRef.current;
    const scroller = scrollerRef.current;
    if (!strip || !scroller) return;

    // Rects rather than offsetWidth/offsetLeft, which round to whole pixels:
    // a tile is 460.8px wide at 1920x1080.
    const [first, second] = Array.from(scroller.children);
    const firstBox = first?.getBoundingClientRect();
    const tileWidth = firstBox?.width ?? 0;
    const stride = firstBox && second ? second.getBoundingClientRect().left - firstBox.left : tileWidth;
    // The `.video-tile-row` rule keeps the row 2rem narrower than the strip.
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const visible = visibleTileCount({
      availableWidth: strip.getBoundingClientRect().width - 2 * rem,
      tileWidth,
      gap: Math.max(0, stride - tileWidth),
      cap: maxVisibleTiles,
      total: scroller.children.length
    });

    setGeometry((previous) =>
      previous.visible === visible && previous.stride === stride ? previous : { visible, stride }
    );
    setStart(firstTileInView(scroller.scrollLeft, stride));
  }, [maxVisibleTiles]);

  // Before paint, so the row never flashes at the wrong width, and again
  // whenever someone joins or leaves or a drawer opens or closes. The
  // ResizeObserver below sees the drawer too, but its update only lands a
  // frame later, after the row has painted at the old count.
  useLayoutEffect(() => {
    measure();
  }, [measure, total, openDrawers.chat, openDrawers.decorations]);

  useEffect(() => {
    const strip = stripRef.current;
    const firstTile = scrollerRef.current?.firstElementChild;
    if (!strip || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(strip);                     // a drawer opening or closing, a window resize
    if (firstTile) observer.observe(firstTile);  // the tile size, which follows vh and vw
    return () => observer.disconnect();
  }, [measure, total]);

  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (scroller) setStart(firstTileInView(scroller.scrollLeft, geometry.stride));
  }, [geometry.stride]);

  const visible = Math.min(geometry.visible, Math.max(1, total));
  const { start: firstInView, canScrollBack, canScrollForward, hiddenAfter } = rowWindow({
    start,
    visible,
    total
  });

  // The arrow or chip that took the row to its end disappears with it. When
  // it had keyboard focus, hand that to the opposite arrow instead of letting
  // it fall back to the page.
  const focusHandoffRef = useRef<'back' | 'forward' | null>(null);

  const scrollByTiles = useCallback((direction: 1 | -1, event: React.MouseEvent<HTMLButtonElement>) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const reachesEnd = direction > 0 ? hiddenAfter <= 1 : firstInView <= 1;
    focusHandoffRef.current =
      reachesEnd && document.activeElement === event.currentTarget
        ? direction > 0 ? 'back' : 'forward'
        : null;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollBy({ left: direction * geometry.stride, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [firstInView, hiddenAfter, geometry.stride]);

  const scrollBack = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => scrollByTiles(-1, event),
    [scrollByTiles]
  );
  const scrollForward = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => scrollByTiles(1, event),
    [scrollByTiles]
  );

  useLayoutEffect(() => {
    const handoff = focusHandoffRef.current;
    if (!handoff) return;
    // Only when focus was actually dropped, never taken from somewhere else.
    const focusDropped = document.activeElement === document.body || document.activeElement === null;
    if (handoff === 'back' && !canScrollForward) {
      focusHandoffRef.current = null;
      if (focusDropped) backButtonRef.current?.focus();
    } else if (handoff === 'forward' && !canScrollBack) {
      focusHandoffRef.current = null;
      if (focusDropped) forwardButtonRef.current?.focus();
    }
  }, [canScrollBack, canScrollForward]);

  return (
    <div
      ref={stripRef}
      className={`${getStackPositionClass(openDrawers)} ${VIDEO_CONFIG.styles.zIndex} pointer-events-none`}
      data-testid="video-tile-grid"
      // Matches LiveKit's unqualified `[data-lk-theme]` base rules while the
      // `--lk-*` tokens come from the game palette in `index.css` (#80).
      data-lk-theme={VIDEO_CONFIG.theme}
    >
      <div
        className="video-tile-row flex flex-col items-end gap-2"
        data-testid="video-tile-row"
        style={{ '--video-row-tiles': visible } as React.CSSProperties}
      >
        <div className="relative w-full">
          {/* The padding, cancelled by the negative margin, leaves room for
              the speaking ring, which draws outside the tile, and the matching
              scroll padding keeps the snap points on the tiles' edges. The
              scrollbar is hidden: the arrows and the chip replace it. */}
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            role="list"
            aria-label="Video call participants"
            className="-m-1 flex gap-[var(--video-tile-gap)] overflow-x-auto overscroll-x-contain p-1 scroll-px-1 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {trackRefs.map((trackRef) => (
              <div key={tileKey(trackRef)} role="listitem" className="shrink-0 snap-start">
                <ParticipantVideoTile
                  trackRef={trackRef}
                  microphoneTrackRef={
                    trackRef.source === Track.Source.ScreenShare
                      ? undefined
                      : microphoneTrackRefs?.get(trackRef.participant.identity)
                  }
                />
              </div>
            ))}
          </div>

          {canScrollBack && (
            <button
              ref={backButtonRef}
              type="button"
              onClick={scrollBack}
              className={`${ARROW_CLASS} left-2`}
              aria-label="Show previous participants"
            >
              <FaChevronLeft aria-hidden="true" />
            </button>
          )}
          {canScrollForward && (
            <button
              ref={forwardButtonRef}
              type="button"
              onClick={scrollForward}
              className={`${ARROW_CLASS} right-2`}
              aria-label="Show more participants"
            >
              <FaChevronRight aria-hidden="true" />
            </button>
          )}
        </div>

        {(actions || hiddenAfter > 0) && (
          <div className="flex items-center gap-2" data-testid="video-tile-row-actions">
            {/* `pointer-events` inherits, and `contents` adds no box of its own. */}
            {actions && <div className="contents pointer-events-auto">{actions}</div>}
            {hiddenAfter > 0 && (
              <button
                type="button"
                onClick={scrollForward}
                className="pointer-events-auto rounded-full bg-game-dark/90 px-3 py-1 text-xs text-game-light shadow-lg border border-gray-700 hover:bg-game-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-game-golden"
                aria-label={`Show ${hiddenAfter} more participants`}
              >
                +{hiddenAfter}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const VideoTileGrid = memo(VideoTileGridComponent);
VideoTileGrid.displayName = 'VideoTileGrid';
